import {
    ChangeDetectionStrategy,
    Component,
    HostBinding,
    Input,
    OnDestroy,
    OnInit,
    ViewChild,
    ViewEncapsulation,
} from '@angular/core';
import {
    FormBuilder,
    FormControl,
    FormGroup,
    Validators,
} from '@angular/forms';
import { AppStateFacade } from '@app-state';
import { UiExecutionSettingsComponent } from '@components';
import {
    enumMapper,
    ExecutionSettingsType,
    IExecutionSetting,
    IRobotProvision,
    RobotTypeAttended,
    UserType,
} from '@models';
import { SettingService } from '@services';
import { identifier } from '@uipath/angular/utilities';
import UiValidators, {
    UI_ROBOT_USERNAME_MAX_LENGTH,
    UI_ROBOT_USERNAME_REGEX,
} from '@validators';

import cloneDeep from 'lodash-es/cloneDeep';
import {
    of,
    ReplaySubject,
    Subject,
} from 'rxjs';
import {
    catchError,
    finalize,
    switchMap,
    takeUntil,
} from 'rxjs/operators';

enum RobotFormControl {
    isExternalLicensed = 'isExternalLicensed',
    userName = 'userName',
    type = 'type',
    executionSettings = 'executionSettings',
}

const EXECUTION_SETTINGS_PERMISSIONS = 'Settings.Edit|Robots.Create|Robots.Edit';

@Component({
    selector: 'ui-user-provision-robot',
    templateUrl: './provision-robot.component.html',
    styleUrls: ['./provision-robot.component.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProvisionRobotComponent implements OnInit, OnDestroy {
    @Input()
    public robot?: IRobotProvision | undefined;

    @Input()
    public get enabled() {
        return this._enabled;
    }
    public set enabled(enabled) {
        this._enabled = enabled;

        if (!this.form) { return; }
        if (enabled) {
            this.form.enable();
        } else {
            this.form.disable();
        }
    }

    @Input()
    public get userType() { return this._userType; }
    public set userType(userType) {
        this._userType = userType;
        if (!this.form) { return; }

        if (!this.hasExecutionSettings) {
            this.form.removeControl(RobotFormControl.executionSettings);
        }

        // reset group license type to development if none is selected
        // for anything else than AD User
        if (
            this._userType !== UserType.DirectoryUser &&
            !this.form.get(RobotFormControl.type)!.value
        ) {
            this.form.get(RobotFormControl.type)!.setValue(RobotTypeAttended.Development);
        }

        // we only have the username on standard users
        if ([UserType.DirectoryGroup, UserType.DirectoryUser, UserType.Robot].includes(this.userType)) {
            this.form.removeControl(RobotFormControl.userName);
        } else {
            this.form.addControl(RobotFormControl.userName, this._userNameControl);
        }
    }

    @ViewChild(UiExecutionSettingsComponent, { static: false })
    public set executionSettingsComponent(value: UiExecutionSettingsComponent) {
        if (
            !value ||
            !!this.form.get(RobotFormControl.executionSettings) ||
            !this.hasExecutionSettings
        ) { return; }

        this.form.addControl(
            RobotFormControl.executionSettings,
            value.formGroup,
        );
    }

    @HostBinding('class.ui-user-provision-robot')
    public className = 'ui-user-provision-robot';

    public readonly RobotFormControl = RobotFormControl;
    public readonly UserType = UserType;
    public readonly RobotTypeAttended = RobotTypeAttended;

    public typeList = enumMapper(RobotTypeAttended, {
        resourceKey: 'RobotType',
    }).map(entry => ({ ...entry, value: Number(entry.value) }));

    public robotTypeId = identifier();

    public form!: FormGroup;
    public executionSettingsConfig$ = new ReplaySubject<IExecutionSetting[]>(1);
    public loadingSettings$ = new Subject();
    public get hasExecutionSettings() {
        return [UserType.User, UserType.DirectoryUser].includes(this.userType);
    }

    private _destroyed$ = new Subject();

    private _userType = UserType.User;
    private _enabled = true;
    private _userNameControl: FormControl;

    constructor(
        private _settingsService: SettingService,
        private _formBuilder: FormBuilder,
        private _appState: AppStateFacade,
    ) {
        this._userNameControl = new FormControl(undefined, [
            Validators.maxLength(UI_ROBOT_USERNAME_MAX_LENGTH),
            UiValidators.regex(UI_ROBOT_USERNAME_REGEX, true),
            UiValidators.required(),
        ]);
    }

    ngOnInit() {
        const robot: IRobotProvision = cloneDeep(this.robot) || {} as IRobotProvision;
        robot.type =
            robot.type != null
                ? robot.type
                : this._userType === UserType.DirectoryUser ? null : RobotTypeAttended.Development;
        robot.executionSettings = robot.executionSettings || {};
        robot.isExternalLicensed = robot.isExternalLicensed || false;

        this.form = this._formBuilder.group({
            [RobotFormControl.type]: robot.type,
        });

        this.form.addControl(RobotFormControl.isExternalLicensed, new FormControl(robot.isExternalLicensed));

        if (this.userType === UserType.User) {
            this._userNameControl.setValue(robot.userName);
            this.form.addControl(RobotFormControl.userName, this._userNameControl);
        }

        // wait for the execution settings configuration to load
        this.form.markAsPending();

        const guardedExecutionSettings$ = this._appState.user
            .permissions.isGranted(EXECUTION_SETTINGS_PERMISSIONS)
            .pipe(
                switchMap(isGranted => isGranted
                    ? this._settingsService.getExecutionSettingsConfiguration(ExecutionSettingsType.Robot)
                    : of({ configuration: [] })),
            );

        guardedExecutionSettings$
            .pipe(
                finalize(() => this.loadingSettings$.next(false)),
                catchError(() => of({ configuration: [] })),
                takeUntil(this._destroyed$),
            ).subscribe(data => {
                this.executionSettingsConfig$.next(data.configuration);

                if (this.form.status === 'PENDING') {
                    this.form.markAsPristine();
                }
                this.enabled = this._enabled;
            });
    }

    ngOnDestroy() {
        this._destroyed$.next();
        this._destroyed$.complete();
    }
}
